'use client';

import { useState, useEffect, useMemo } from 'react';
import DataTable from '../../../../components/DataTable';
import ActionMenu from '../../../../components/ActionMenu';
import { useNotifier } from '../../../../components/Notifier';
import DeleteConfirmDialog from '../../../../components/DeleteConfirmDialog';

import { Chip } from '@mui/material';
import MasterBrandAddEdit from './MasterBrandAddEdit';
import { useLanguage } from '../../../../utils/i18n';
import { useTireBrandDataList, deleteTireBrand } from '../../../../stores/tireBrandStore';
import { useCompanyList } from '../../../../stores/companyStore';
import { fleetApi } from '../../../../api/axiosClient';

function MasterBrandPageContent() {
  const currentUserRole = typeof window !== 'undefined' ? localStorage.getItem('rolename') || localStorage.getItem('role_name') || '' : '';
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const notify = useNotifier();
  const { t } = useLanguage();

  const [selectedCompanies, setSelectedCompanies] = useState(() => {
    const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
    return stored ? [{ value: String(stored), label: String(stored) }] : [];
  });

  const filterCompanyIds = useMemo(() => {
    if (!selectedCompanies || selectedCompanies.length === 0) return '';
    const hasAll = selectedCompanies.some((s) => String(s.value) === 'all');
    if (hasAll) return '';
    return selectedCompanies.map((s) => String(s.value)).join(',');
  }, [selectedCompanies]);

  const { companyOptions, isLoading: companyLoading } = useCompanyList();

  const companyOptionsNormalized = useMemo(
    () =>
      (companyOptions || []).map((c) => ({
        value: String(c.value ?? c.id ?? c),
        label: c.label ?? c.name ?? String(c.value ?? c.id ?? c)
      })),
    [companyOptions]
  );

  const handleCompanyChange = (options) => {
    setSelectedCompanies(options);
    setPaginationModel((p) => ({ ...p, page: 0 }));
  };

  const [searchField, setSearchField] = useState('name');
  const [searchRaw, setSearchRaw] = useState('');
  const [searchValue, setSearchValue] = useState('');

  const searchFieldOptions = useMemo(
    () => [
      { value: 'all', label: t('general.searchAll', 'All') },
      { value: 'code_internal', label: t('brand.code', 'Brand Code') },
      { value: 'name', label: t('brand.name', 'Brand Name') },
      { value: 'manufacture', label: t('brand.manufacture', 'Manufacture') },
      { value: 'country', label: t('brand.origin', 'Country') }
    ],
    [t]
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchValue(String(searchRaw ?? '').trim());
      setPaginationModel((p) => ({ ...p, page: 0 }));
    }, 700);
    return () => clearTimeout(timeoutId);
  }, [searchRaw, searchField]);

  const {
    data: rows,
    totalRecord: totalRecords,
    isLoading,
    refetch
  } = useTireBrandDataList(paginationModel.page, paginationModel.pageSize, searchField, searchValue, filterCompanyIds);

  const handleFetchAllForExport = async () => {
    try {
      const length = totalRecords > 0 ? totalRecords : 9999;
      const columns = [];
      const fetchFilters = [];

      if (searchRaw && searchRaw.trim() !== '') {
        if (searchField === 'all') {
          fetchFilters.push(
            { name: 'code', value: searchRaw },
            { name: 'name', value: searchRaw },
            { name: 'name', table_name: 'm_manufacture', value: searchRaw },
            { name: 'name', table_name: 'm_country', value: searchRaw }
          );
        } else {
          if (searchField === 'manufacture') {
            fetchFilters.push({ name: 'name', table_name: 'm_manufacture', logic_operator: 'like', operator: 'AND', value: searchRaw });
          } else if (searchField === 'country') {
            fetchFilters.push({ name: 'name', table_name: 'm_country', logic_operator: 'like', operator: 'AND', value: searchRaw });
          } else {
            fetchFilters.push({ name: searchField, logic_operator: 'like', operator: 'AND', value: searchRaw });
          }
        }
      }

      const payload = {
        paging: { start: 0, length },
        orders: { columns: ['name'], ascending: true },
        joins: [
          { name: 'manufacture', column_results: ['name'] },
          { name: 'country', column_results: ['name'] }
        ],
        columns: columns
      };

      if (filterCompanyIds) {
        if (filterCompanyIds.includes(',')) {
          fetchFilters.push({ name: 'company', logic_operator: 'in', operator: 'AND', value: filterCompanyIds });
        } else {
          fetchFilters.push({ name: 'company', logic_operator: '=', operator: 'AND', value: filterCompanyIds });
        }
      }

      if (fetchFilters.length > 0) {
        if (searchField === 'all') {
          payload.filters = fetchFilters;
        } else {
          payload.columns = [...payload.columns, ...fetchFilters];
        }
      }

      const resp = await fleetApi.post('/tire-brand/search', payload);
      const data = resp?.data ?? null;
      return Array.isArray(data) ? data : (data?.data ?? []);
    } catch (err) {
      console.error('brand export all error', err);
      return [];
    }
  };

  const handlePageChange = (model) => {
    setPaginationModel((prev) => {
      if (prev.page === model.page && prev.pageSize === model.pageSize) return prev;
      return model;
    });
  };

  const handleAddClick = () => {
    setEditingId(null);
    setOpenDialog(true);
  };

  const handleEditClick = (id) => {
    setEditingId(id);
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditingId(null);
  };

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTargetId, setConfirmTargetId] = useState(null);
  const [confirmName, setConfirmName] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);

  const handleDelete = (id, itemName) => {
    setConfirmTargetId(id);
    setConfirmName(itemName || '');
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    if (confirmLoading) return;
    setConfirmOpen(false);
    setConfirmTargetId(null);
    setConfirmName('');
  };

  const handleConfirmDelete = async () => {
    if (!confirmTargetId) return;
    setConfirmLoading(true);
    try {
      await deleteTireBrand(confirmTargetId);
      notify({ message: t('brand.notifSuccess', 'Brand successfully deleted'), severity: 'success' });
      refetch();
    } catch (err) {
      console.error(err);
      notify({ message: t('brand.notifFailed', 'Failed to delete Brand'), severity: 'error' });
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setConfirmTargetId(null);
      setConfirmName('');
    }
  };

  const columns = [
    {
      field: 'code',
      headerName: t('brand.code', 'Brand Code'),
      flex: 1,
      valueGetter: (value, row) => row?.code_internal || '-'
    },
    { field: 'name', headerName: t('brand.name', 'Brand Name'), flex: 1.5 },
    {
      field: 'manufacture',
      headerName: t('brand.manufacture', 'Manufacture'),
      flex: 1.5,
      valueGetter: (value, row) => row?.join?.manufacture_name || row?.manufacture_name || '-'
    },
    {
      field: 'country',
      headerName: t('brand.origin', 'Country'),
      flex: 1,
      valueGetter: (value, row) => row?.join?.country_name || row?.country_name || '-'
    },
    {
      field: 'status',
      headerName: t('masterCompany.status', 'Status'),
      flex: 0.5,
      renderCell: (params) => {
        const isActive = params.row.status === 'Active';
        return (
          <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Chip
              label={isActive ? 'Active' : 'Inactive'}
              color={isActive ? 'success' : 'default'}
              size="small"
              sx={{
                fontWeight: 600,
                minWidth: 70,
                backgroundColor: isActive ? 'rgba(46, 125, 50, 0.1)' : 'rgba(0, 0, 0, 0.08)',
                color: isActive ? '#2e7d32' : 'text.secondary'
              }}
            />
          </div>
        );
      }
    },
    ...(currentUserRole === 'Administrator'
      ? [
          { field: 'created_by', headerName: t('general.createdBy', 'Created By'), flex: 0.5, hide: true },
          { field: 'created_date', headerName: t('general.createdDate', 'Created Date'), flex: 0.5, hide: true },
          { field: 'modified_by', headerName: t('general.modifiedBy', 'Modified By'), flex: 0.5, hide: true },
          { field: 'modified_date', headerName: t('general.modifiedDate', 'Modified Date'), flex: 0.5, hide: true }
        ]
      : []),
    {
      field: 'actions',
      headerName: t('general.actions', 'Actions'),
      sortable: false,
      headerAlign: 'center',
      width: 120,
      renderCell: (params) => (
        <ActionMenu row={params.row} onEdit={handleEditClick} onDelete={(id) => handleDelete(id, params.row.name)} mode="inline" />
      )
    }
  ];

  return (
    <div>
      <DataTable
        title={t('brand.title', 'Master Brand')}
        onFetchAllForExport={handleFetchAllForExport}
        rows={rows || []}
        columns={columns}
        loading={isLoading}
        pageSizeOptions={[10, 20, 50, 100]}
        paginationMode="server"
        rowCount={totalRecords}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePageChange}
        searchQuery={searchRaw}
        onSearch={({ field, value }) => {
          setSearchField(field);
          setSearchRaw(value ?? '');
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        searchFieldOptions={searchFieldOptions}
        selectedSearchField={searchField}
        onSearchFieldChange={(f) => {
          setSearchField(f);
          setSearchRaw('');
          setSearchValue('');
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        onAddClick={handleAddClick}
        searchLabel={t('brand.searchBrand', 'Search Brand')}
        companyOptions={companyOptionsNormalized}
        companyLoading={companyLoading}
        selectedCompanies={selectedCompanies}
        onCompanyChange={handleCompanyChange}
      />

      {openDialog && <MasterBrandAddEdit open={openDialog} id={editingId} onClose={handleDialogClose} onSuccess={() => refetch()} />}

      <DeleteConfirmDialog
        open={confirmOpen}
        loading={confirmLoading}
        onClose={handleConfirmClose}
        onConfirm={handleConfirmDelete}
        title={t('deleteConfirm.title', 'Confirm Delete')}
        message={t('deleteConfirm.message', 'Are you sure you want to delete this item?')}
        confirmLabel={t('deleteConfirm.label', 'Delete')}
        cancelLabel={t('deleteConfirm.cancel', 'Cancel')}
        itemName={confirmName}
      />
    </div>
  );
}

export default function MasterBrandPage() {
  return <MasterBrandPageContent />;
}
